import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Switch,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getProcessById, updateProcessUrgency } from "../../services/processService";
import { syncProcessDuimp } from "../../services/siscomexService";
import { ProcessItemViewModel, ProcessViewModel } from "../../models/Process";
import { useMediaQuery } from "react-responsive";
import { DateTime } from "luxon";
import { Event, EventAvailable } from "@mui/icons-material";
import Prop from "./components/Prop";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { RiShip2Fill } from "react-icons/ri";
import { FaAngleRight, FaRegFileAlt, FaSyncAlt } from "react-icons/fa";
import Layout from "../../components/Layout";
import { crm } from "../../theme";
import ProcessUrgencyBadge from "../../components/ProcessUrgencyBadge";
import NumeraryPanel from "./components/NumeraryPanel";
import ExchangeNumeraryPanel from "./components/ExchangeNumeraryPanel";
import EstimatedRealizedPanel from "./components/EstimatedRealizedPanel";
import DuimpPanel from "./components/DuimpPanel";
import CargoPanel from "./components/CargoPanel";
import SettlementPanel from "./components/SettlementPanel";
import FiscalInvoicesPanel from "./components/FiscalInvoicesPanel";
import GovRegistrationPanel from "./components/GovRegistrationPanel";
import { duimpSituation, toneColors } from "./components/duimp";
import SectionCard from "./components/SectionCard";
import StatusTimelineSection from "../../components/StatusTimeline";
import PendingsPanel from "./components/PendingsPanel";
import { formatNcm, TableCard, tdProps, thProps } from "./components/tableStyles";
import { FaBoxOpen, FaBriefcase } from "react-icons/fa";
import { hasScreenAccess } from "../../utils/screens";

const Process = () => {
  const { register } = useForm();
  const [outEditing, setOutEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [process, setProcess] = useState<ProcessViewModel>();
  const duimpStatus = duimpSituation(process?.duimpSituation);
  const [savingUrgency, setSavingUrgency] = useState(false);
  const [syncingDuimp, setSyncingDuimp] = useState(false);
  const toast = useToast();

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");
  const isDashboard =
    new URLSearchParams(location.search).get("isDashboard") === "true";
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const navigate = useNavigate();
  const auth = useAuthUser<any>();

  const get = async () => {
    if (id) {
      setLoading(true);
      const response = await getProcessById(id);
      if (response) setProcess(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    get();
  }, [id]);

  const toggleUrgency = async (next: boolean) => {
    if (!process?.id) return;
    const previous = Boolean(process.isUrgent);
    setProcess({ ...process, isUrgent: next });
    setSavingUrgency(true);
    try {
      const result = await updateProcessUrgency(process.id, next);
      if (!result?.success) throw new Error(result?.notifications?.[0]);
      toast({ title: next ? "Processo marcado como urgente" : "Urgência removida", status: "success" });
    } catch (error: any) {
      setProcess({ ...process, isUrgent: previous });
      toast({ title: "Não foi possível alterar a urgência", description: error?.message, status: "error" });
    } finally {
      setSavingUrgency(false);
    }
  };
  // Consulta imediata no Portal Único: só equipe CVF (clientes não gastam a cota do certificado)
  const syncDuimp = async () => {
    if (!process?.id) return;
    setSyncingDuimp(true);
    try {
      const result = await syncProcessDuimp(process.id);
      if (result.error) {
        const [code, ...rest] = result.error.split(": ");
        toast({
          title: code === "COOLDOWN" ? "Aguarde para consultar de novo" : "Falha ao consultar o Portal Único",
          description: rest.join(": ") || result.error,
          status: code === "COOLDOWN" ? "warning" : "error",
        });
      } else {
        toast({
          title: result.changes.length ? "DUIMP atualizada" : "DUIMP consultada, sem alterações",
          description: result.changes.length ? result.changes.join(" · ") : undefined,
          status: "success",
        });
      }
      await get();
    } catch {
      toast({ title: "Falha ao consultar o Portal Único", status: "error" });
    } finally {
      setSyncingDuimp(false);
    }
  };
  // Atalho p/ o cadastro comercial do cliente: só equipe com acesso a Clientes Ativos (cliente nunca vê)
  const canOpenCommercialClient = Boolean(process?.clientId) && hasScreenAccess(auth, "/commercial/clients");
  const canSyncDuimp = auth?.position !== 0 && auth?.position !== 1 && /BR/i.test(process?.di ?? "");

  const values = [
    {
      title: "Data de Registro:",
      name: "registrationDate",
      value: DateTime.fromISO(process?.registrationDate ?? "").isValid
        ? DateTime.fromISO(process?.registrationDate ?? "")?.toFormat(
            "dd/LL/yyyy"
          )
        : undefined,
    },
    {
      title: "Frete:",
      value: process?.freight,
      name: "freight",
    },
    {
      title: "Incoterm:",
      value: process?.incoterm,
      name: "incoterm",
    },
    {
      title: "Peso Líq.:",
      value: process?.weight,
      name: "weight",
    },
    {
      title: "Armador:",
      value: process?.customsAgent,
      name: "customsAgent",
    },
    { title: "Fatura:", value: process?.invoice, name: "invoice" },
    { title: "LI:", value: process?.li, name: "li" },
    { title: "Validade:", value: process?.validity, name: "validity" },
    {
      title: "Documento de Envio:",
      value: process?.shippingDocument,
      name: "shippingDocument",
    },
    { title: "Embarcação:", value: process?.vessel, name: "vessel" },
    {
      title: "Porto de Descarga:",
      value: process?.dischargePort,
      name: "dischargePort",
    },
    { title: "DI:", value: process?.di, name: "di" },
    { title: "CE Mercante:", value: process?.ceMerchant, name: "ceMerchant" },
    { title: "BL/AWB/CRT Nº:", value: process?.blAwbCrt, name: "blAwbCrt" },
    { title: "Demurrage:", value: process?.demurrage, name: "demurrage" },
    { title: "Agente de cargas", value: process?.carrier, name: "carrier" },
  ];

  const columns = [
    {
      label: "NCM",
      data: (p: ProcessItemViewModel) => <Text fontWeight="600">{formatNcm(p?.ncm)}</Text>,
    },
    {
      label: "Produto",
      data: (p: ProcessItemViewModel) => (
        <Text color={p?.product ? undefined : crm.textFaint}>{p?.product || "—"}</Text>
      ),
    },
    {
      label: "Quantidade",
      data: (p: ProcessItemViewModel) => <Text>{p?.quantity || "—"}</Text>,
    },
    {
      label: "Valor unitário",
      data: (p: ProcessItemViewModel) => <Text>{p?.unitValue || "—"}</Text>,
    },
  ];
  // ClientStatus (position 1) não vê valores do processo: valor unitário, total invoice, DUIMP, numerários
  const canSeeValues = auth?.position !== 1;
  const visibleColumns = columns.filter((c) =>
    canSeeValues ? true : c.label !== "Valor unitário"
  );
  return (
    <Box height="100%">
      <Layout>
        <Flex
          backgroundColor="var(--main-bg-color)"
          overflowY="scroll"
          flexDirection="column"
          pt="0"
          alignItems="center"
        >
          <Flex flexDir="column" mb="100px" width={isMobile ? "90%" : "90%"}>
            <Flex
              alignItems="center"
              mb="48px"
              pt="0"
              gap="8px"
              mt={{ base: "16px", md: "27px" }}
              width="90%"
            >
              <Link
                color="var(--icon-color)"
                onClick={() =>
                  navigate(isDashboard ? "/dashboard" : "/processes")
                }
                size="md"
              >
                {isDashboard ? "Dashboard" : "Processos"}
              </Link>
              <FaAngleRight />
              <Text size="md">Detalhes</Text>
            </Flex>
            <Flex align="center" justify="space-between" gap="16px" flexWrap="wrap">
              <HStack mb="32px" spacing="12px">
              <Heading size="lg" fontWeight="normal">
                Detalhes do Processo {process?.cvfRef}
                {process?.cvfSubRef}
              </Heading>
              {process?.isUrgent && <ProcessUrgencyBadge />}
              {canOpenCommercialClient && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FaBriefcase />}
                  borderColor={crm.border}
                  color={crm.text}
                  onClick={() => navigate(`/commercial/clients?companyId=${process?.clientId}`)}
                  title={`Abrir ${process?.clientName ?? "cliente"} em Comercial › Clientes Ativos`}
                >
                  Clientes Ativos
                </Button>
              )}
              </HStack>
              {auth?.position !== 1 && (
                <HStack mb="32px" p="10px" bg={process?.isUrgent ? "rgba(220,38,38,0.06)" : "white"} borderRadius="md">
                  <Text fontWeight="semibold">Processo urgente</Text>
                  <Switch
                    colorScheme="red"
                    isChecked={Boolean(process?.isUrgent)}
                    isDisabled={savingUrgency}
                    onChange={(event) => toggleUrgency(event.target.checked)}
                    aria-label="Alterar urgência do processo"
                  />
                </HStack>
              )}
            </Flex>
            <Flex gap="12px" w="100%" flexDir={{ base: "column", md: "row" }}>
              {auth?.position !== 1 && (
                <Flex className="dashboard-card" w="100%">
                  <Heading size="md" fontWeight="normal">
                    Total invoice
                  </Heading>
                  <Heading size="xl" mt="16px">
                    {process?.totalInvoice}
                  </Heading>
                </Flex>
              )}
              <Flex
                className="dashboard-card"
                w="100%"
                gap="8px"
                alignItems="center"
                cursor="pointer"
                _hover={{ backgroundColor: "var(--hover-color)" }}
                onClick={() => navigate(`/tracking?id=${id}`)}
              >
                <RiShip2Fill color="var(--icon-color)" fontSize="32px" />
                <Heading size="lg" fontWeight="normal" textAlign="center">
                  Rastrear
                </Heading>
              </Flex>

              {auth?.position !== 1 && (
                <Flex
                  className="dashboard-card"
                  w="100%"
                  gap="8px"
                  cursor="pointer"
                  alignItems="center"
                  _hover={{ backgroundColor: "var(--hover-color)" }}
                  onClick={() => navigate(`/documents?id=${id}`)}
                >
                  <FaRegFileAlt color="var(--icon-color)" fontSize="32px" />
                  <Heading size="lg" fontWeight="normal" textAlign="center">
                    Ver documentos
                  </Heading>
                </Flex>
              )}
              {canSeeValues && (process?.financialItems?.length ?? 0) > 0 && (
                <Flex
                  className="dashboard-card"
                  w="100%"
                  gap="8px"
                  alignItems="center"
                  cursor="pointer"
                  _hover={{ backgroundColor: "var(--hover-color)" }}
                  onClick={() => navigate(`/financial?id=${id}`)}
                >
                  <Box
                    color="var(--icon-color)"
                    fontSize="32px"
                    className="material-symbols-outlined"
                  >
                    account_balance
                  </Box>
                  <Heading size="lg" fontWeight="normal" textAlign="center">
                    Financeiro
                  </Heading>
                </Flex>
              )}
            </Flex>
            <Flex
              flexWrap="wrap"
              gap="1.5vh"
              backgroundColor="white"
              borderRadius="8px"
              padding="32px"
              width="100%"
              mt="24px"
            >
              <VStack alignItems="flex-start" gap="48px" w="100%" minW={0}>
                <Flex
                  flexDir={{ base: "column", md: "row" }}
                  flexWrap="wrap"
                  alignItems={{ base: "flex-start", md: "center" }}
                  columnGap={{ base: "32px", md: "48px" }}
                  rowGap="16px"
                  w="100%"
                >
                  {DateTime.fromISO(process?.shippingDate ?? "").isValid && (
                    <Flex alignItems="center" gap="15px">
                      <Heading size="lg" fontWeight="normal">
                        ETD{" "}
                      </Heading>
                      <Event color="primary" fontSize="large" />{" "}
                      <Heading size="lg" fontWeight="normal">
                        {DateTime.fromISO(
                          process?.shippingDate ?? ""
                        )?.toFormat("dd/LL/yyyy")}
                      </Heading>
                    </Flex>
                  )}
                  {DateTime.fromISO(process?.arrivalDate ?? "").isValid && (
                    <Flex alignItems="center" gap="15px">
                      <Heading size="lg" fontWeight="normal">
                        ETA{" "}
                      </Heading>
                      <EventAvailable color="primary" fontSize="large" />{" "}
                      <Heading size="lg" fontWeight="normal">
                        {DateTime.fromISO(process?.arrivalDate ?? "")?.toFormat(
                          "dd/LL/yyyy"
                        )}
                      </Heading>
                    </Flex>
                  )}
                  {(process?.channel?.length ?? 0) > 1 && (
                    <Flex
                      alignItems="center"
                      gap="15px"
                      backgroundColor={
                        process?.channel?.toLowerCase() === "verde"
                          ? "#27AE60"
                          : process?.channel?.toLowerCase() === "amarelo"
                          ? "#F1C40F"
                          : "#E74C3C"
                      }
                      color="white"
                      p="8px"
                    >
                      <Heading size="md" fontWeight="normal">
                        CANAL {process?.channel?.toUpperCase()}
                      </Heading>
                    </Flex>
                  )}
                  {duimpStatus && (
                    <Flex
                      direction="column"
                      justify="center"
                      px="12px"
                      py="6px"
                      borderRadius="8px"
                      bg={toneColors[duimpStatus.tone].bg}
                      color={toneColors[duimpStatus.tone].color}
                      title="Consultado automaticamente no Portal Único Siscomex"
                    >
                      <Text fontSize="14px" fontWeight="700">
                        DUIMP · {duimpStatus.label}
                      </Text>
                      {process?.duimpCheckedAt && (
                        <Text fontSize="11px" opacity={0.8}>
                          Portal Único ·{" "}
                          {DateTime.fromISO(process.duimpCheckedAt).toFormat(
                            "dd/LL/yyyy HH:mm"
                          )}
                        </Text>
                      )}
                    </Flex>
                  )}
                  {canSyncDuimp && (
                    <Button
                      size="sm"
                      variant="outline"
                      alignSelf="center"
                      borderColor={crm.border}
                      color={crm.text}
                      leftIcon={<FaSyncAlt />}
                      isLoading={syncingDuimp}
                      loadingText="Consultando"
                      onClick={syncDuimp}
                      title="Consultar esta DUIMP agora no Portal Único Siscomex"
                    >
                      Sincronizar DUIMP
                    </Button>
                  )}
                  {process?.importMode && (
                    <Flex
                      alignItems="center"
                      gap="15px"
                      backgroundColor="var(--icon-color)"
                      color="white"
                      p="8px"
                    >
                      <Heading size="md" fontWeight="normal">
                        {process?.importMode}
                      </Heading>
                    </Flex>
                  )}
                </Flex>
                {auth.admin && (
                  <Button
                    backgroundColor={crm.brand}
                    _hover={{ backgroundColor: crm.brandDark }}
                    color="white"
                    borderRadius="10px"
                    onClick={() => navigate(`/saveProcess?id=${process?.id}`)}
                  >
                    Editar
                  </Button>
                )}
                <Flex
                  flexDir={{ base: "column", md: "row" }}
                  flexWrap="wrap"
                  columnGap="48px"
                  rowGap="32px"
                  backgroundColor="white"
                  w="100%"
                  minW={0}
                >
                  {values
                    .filter((v) => (v.value?.length ?? 0) > 0)
                    .map((v) => (
                      <Prop
                        key={v.name}
                        isMobile={isMobile}
                        get={get}
                        register={register}
                        title={v.title}
                        value={v.value}
                        name={v.name}
                        process={process}
                        outEditing={outEditing}
                      />
                    ))}
                  <Prop
                    isMobile={isMobile}
                    get={get}
                    register={register}
                    title={"Exportador:"}
                    value={process?.exporter}
                    name={"exporter"}
                    process={process}
                    outEditing={outEditing}
                  />
                  <Prop
                    isMobile={isMobile}
                    get={get}
                    register={register}
                    title={"Nº do Container:"}
                    value={process?.containerNumber}
                    name={"exporter"}
                    process={process}
                    outEditing={outEditing}
                  />
                </Flex>
              </VStack>
            </Flex>
            {canSeeValues && process?.duimpDetails && (
              <DuimpPanel
                processId={process.id!}
                details={process.duimpDetails}
                situation={process.duimpSituation}
                channel={process.channel}
              />
            )}
            {process?.id && auth?.position !== 0 && auth?.position !== 1 && process?.blAwbCrt && (
              <CargoPanel process={process} onSynced={get} />
            )}
            {process?.id && auth?.position !== 0 && auth?.position !== 1 && (
              <GovRegistrationPanel processId={process.id} onDone={get} />
            )}
            {canSeeValues && process?.id && <NumeraryPanel processId={process.id} />}
            {process?.id && auth?.position !== 0 && auth?.position !== 1 && (process?.financialItems?.length ?? 0) > 0 && (
              <ExchangeNumeraryPanel processId={process.id} />
            )}
            {process?.id && <StatusTimelineSection processId={process.id} />}
            {process?.id && auth?.position >= 2 && <PendingsPanel processId={process.id} />}
            {canSeeValues && process?.id && <EstimatedRealizedPanel processId={process.id} />}
            {canSeeValues && process?.id && auth?.position !== 0 && auth?.position !== 1 && <SettlementPanel processId={process.id} />}
            {canSeeValues && process?.id && auth?.position !== 0 && auth?.position !== 1 && <FiscalInvoicesPanel processId={process.id} />}
            {(process?.items?.length ?? 0) > 0 && (
              <Box mb="32px">
                <SectionCard
                  icon={<FaBoxOpen />}
                  title="Itens do processo"
                  subtitle={`${process?.items?.length} ${process?.items?.length === 1 ? "item" : "itens"}`}
                >
                  <TableCard>
                    <Thead>
                      <Tr>
                        {visibleColumns.map((column) => (
                          <Th key={column.label} {...thProps}>{column.label}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {process?.items?.map((row) => (
                        <Tr key={row.id} _hover={{ bg: crm.brandSoft }}>
                          {visibleColumns.map((column) => (
                            <Td key={column.label} {...tdProps}>{column.data(row)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </TableCard>
                </SectionCard>
              </Box>
            )}
          </Flex>
        </Flex>
      </Layout>
    </Box>
  );
};

export default Process;
